import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import {
  apiClient,
  AssistantConversationOut,
  AssistantNoConversationOut,
  GetFixedPromptTextGenerationOutState,
  NotFoundError,
} from 'lib/apiClient'
import { QueryKey } from 'lib/queryClient'

export interface LatestAssistantChatMessageHook {
  data: AssistantConversationOut | AssistantNoConversationOut | undefined
  isLoading: boolean
  isError: boolean
  retry: () => void
}

export function useLatestAssistantChatMessage(
  enabled: boolean = true,
): LatestAssistantChatMessageHook {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [QueryKey.assistantTextGeneration],
    queryFn: async () => {
      // BE will return 404 if the user has never interacted with the assistant before
      // As such, we need to handle this case manually since it is intended behavior
      try {
        return await apiClient.getLatestAssistantChatMessage()
      } catch (e) {
        if (e instanceof NotFoundError) {
          const noConversationData: AssistantNoConversationOut = {
            correlationId: null,
            dialogueSnapshotId: null,
            state: GetFixedPromptTextGenerationOutState.Empty,
            result: null,
          }
          return noConversationData
        }
        throw e
      }
    },
    enabled,
  })

  return useMemo(
    () => ({
      data,
      isLoading,
      isError,
      retry: refetch,
    }),
    [data, isError, isLoading, refetch],
  )
}
